import { ReactElement, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { VStack, HStack, Text, Box } from '@chakra-ui/react';
import { useGetClassWorksheets } from 'hooks/api/backend/managament/getClasses';
import { EducationIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { PageContext } from 'components/common/PageContext';
import { useBreadcrumbs } from 'hooks/utils/useBreadcrumbs';
import { ProtectedRoutes } from 'types/Routes';
import { PageSpinner } from 'components/atoms/PageSpinner';
import { QueryErrorState } from 'components/common/QueryErrorState';
import { ClassWorksheetsContent } from 'components/worksheet/class/ClassWorksheetsContent';
import { ClassParticipantsContent } from 'components/worksheet/class/ClassParticipantsContent';
import { getWorksheetDashboardBreadcrumbPath } from 'utils/worksheetDashboardReturnParams';

const tabs = [
  { id: 'worksheets', label: 'Arbeitsblätter' },
  { id: 'participants', label: 'Teilnehmende' },
];

export const WorksheetClassDetail = (): ReactElement => {
  const { id } = useParams<{ id: string }>();

  const [activeTab, setActiveTab] = useState<string>('worksheets');

  const { addBreadcrumbs, removeBreadcrumbs } = useBreadcrumbs();

  const { data: classData, isLoading: isLoadingClass, isError, error, refetch } =
    useGetClassWorksheets(id || '');

  useEffect(() => {
    if (classData) {
      addBreadcrumbs([
        {
          path: getWorksheetDashboardBreadcrumbPath(),
          name: 'Arbeitsblatt-Verwaltung',
        },
        {
          path: `${ProtectedRoutes.WORKSHEET_CLASS_DETAIL}/${id}`,
          name: classData.name,
        },
      ]);
    }
    return () => {
      removeBreadcrumbs();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classData]);

  const handleTabChange = (tabId: string): void => {
    setActiveTab(tabId);
  };

  const renderTabs = (): JSX.Element => {
    return (
      <HStack gap="0" align="stretch">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;
          return (
            <Box
              key={tab.id}
              as="button"
              onClick={() => handleTabChange(tab.id)}
              width="140px"
              cursor="pointer"
              _hover={{
                opacity: isActive ? '100' : '70',
              }}
              transition="opacity 0.2s"
            >
              <VStack gap="10px" align="center">
                <Text textStyle="body.s.medium" color={isActive ? 'text.accent' : 'text.tertiary'}>
                  {tab.label}
                </Text>
                <Box
                  height="2px"
                  width="100%"
                  bg={isActive ? 'background.accent' : 'transparent'}
                  borderRadius="1px"
                />
              </VStack>
            </Box>
          );
        })}
      </HStack>
    );
  };

  const renderTabContent = (): JSX.Element | null => {
    switch (activeTab) {
      case 'worksheets':
        return (
          <ClassWorksheetsContent
            worksheets={classData?.worksheets}
            classId={id || ''}
            className={classData?.name || ''}
          />
        );
      case 'participants':
        return <ClassParticipantsContent classId={id || ''} students={classData?.students || []} />;

      default:
        return null;
    }
  };

  if (isError) {
    return <QueryErrorState error={error} onRetry={() => refetch()} />;
  }

  if (isLoadingClass || !classData) {
    return <PageSpinner />;
  }

  return (
    <PageContext title="Kurs Details" hasBackButton headTitle="Kurs Details">
      <VStack
        align="left"
        bg="background.primary"
        w="full"
        gap="l"
        px="l"
        pt="l"
        borderTopRadius="15px"
        mb="l"
      >
        <HStack gap="5px">
          <Text
            textStyle="h3.medium"
            color="text.primary"
            lineClamp={1}
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {classData?.name}
          </Text>
        </HStack>

        <VStack gap="34px" align="flex-start">
          <VStack gap="10px" align="flex-start">
            {/* Teachers Info */}
            <VStack align="flex-start" gap="10px">
              <HStack gap="5px" align="center">
                <NovaIcon icon={EducationIcon} size="m" color="icon.secondary" aria-hidden />
                <Text textStyle="body.s.regular" color="text.secondary">
                  Lehrende:
                </Text>
                <Box display="flex" flexWrap="wrap" gap="xs" width="100%">
                  <Text textStyle="body.s.regular" color="text.secondary">
                    {classData?.teachers
                      ?.map((teacher) => `${teacher.firstName} ${teacher.lastName}`)
                      .join(', ')}
                  </Text>
                </Box>
              </HStack>
            </VStack>
            {/* Guides Info */}
            <VStack align="flex-start" gap="10px">
              <HStack gap="5px" align="center">
                <NovaIcon icon={EducationIcon} size="m" color="icon.secondary" aria-hidden />
                <Text textStyle="body.s.regular" color="text.secondary">
                  Praxisanleitende:
                </Text>
                <Box display="flex" flexWrap="wrap" gap="8px" w="100%">
                  <Text textStyle="body.s.regular" color="text.secondary">
                    {classData?.guides
                      ?.map((guide) => `${guide.firstName} ${guide.lastName}`)
                      .join(', ')}
                  </Text>
                </Box>
              </HStack>
            </VStack>
          </VStack>

          {renderTabs()}
        </VStack>
      </VStack>
      {/* Content Area */}
      <Box p="l" bg="background.primary" borderRadius={{ md: '10px' }} width="100%">
        {renderTabContent()}
      </Box>
    </PageContext>
  );
};
